import { Link } from "react-router-dom";
import { ArrowRight, Fingerprint, ShieldCheck, Smartphone, Trophy, Users, Dices } from "lucide-react";
import { RollingPair } from "@/components/Dice";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { formatXAF } from "@/lib/api";
import { InstallApp } from "@/components/InstallApp";
import { WhatsAppIcon, whatsappUrl } from "@/components/ContactWhatsApp";
import { GamesShowcase } from "@/components/GamesShowcase";

const Hero = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  return (
    <section className="relative grid items-center gap-10 py-6 md:grid-cols-[1.2fr_1fr] md:py-16">
      <div className="rise">
        <p className="mb-5 text-xs font-semibold uppercase tracking-[0.2em] text-amber-400" data-testid="landing-overline">
          {t("landing.overline")}
        </p>
        <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
          {t("landing.title1")}
          <br />
          <span className="text-slate-400">{t("landing.title2")}</span>
          <br />
          <span className="text-amber-400">{t("landing.title3")}</span>
        </h1>
        <p className="mt-6 max-w-xl text-sm leading-relaxed text-slate-300 sm:text-base">{t("landing.subtitle")}</p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link
            to={user ? "/lobby" : "/auth?mode=signup"}
            className="pulse-ring inline-flex h-14 items-center justify-center gap-2 rounded-full bg-amber-500 px-8 font-display text-base font-bold text-[#0B0D12] transition-transform duration-150 hover:bg-amber-400 active:scale-95"
            data-testid="landing-play-btn"
          >
            {t("landing.cta")} <ArrowRight className="h-5 w-5" />
          </Link>
          <Link
            to="/wallet"
            className="inline-flex h-14 items-center justify-center rounded-full border border-white/15 px-8 font-semibold text-slate-200 transition-colors hover:border-amber-500/50 hover:text-white"
            data-testid="landing-wallet-btn"
          >
            {t("landing.ctaWallet")}
          </Link>
        </div>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <Link
            to="/demo"
            className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/15 px-6 text-sm font-semibold text-slate-200 transition-colors hover:border-amber-500/50 hover:text-white"
            data-testid="landing-demo-btn"
          >
            <Dices className="h-4 w-4 text-amber-400" /> {t("demo.tryFree")}
          </Link>
          <InstallApp />
        </div>
        <p className="mt-6 text-xs text-slate-500" data-testid="landing-adult-notice">{t("landing.adult")}</p>
      </div>
      <div className="relative flex min-h-[260px] items-center justify-center">
        <div className="absolute h-64 w-64 rounded-full bg-amber-500/20 blur-[90px]" />
        <RollingPair size={110} />
      </div>
    </section>
  );
};

const Rules = () => {
  const { t } = useI18n();
  const items = [
    { icon: Users, k: "rule1" },
    { icon: Dices, k: "rule2" },
    { icon: Trophy, k: "rule3" },
  ];
  return (
    <section className="py-10" data-testid="landing-rules">
      <h2 className="mb-6 font-display text-xl font-bold tracking-tight sm:text-2xl">{t("landing.rulesTitle")}</h2>
      <div className="grid gap-4 md:grid-cols-3">
        {items.map(({ icon: Icon, k }, i) => (
          <div key={k} className="surface rise p-6" style={{ animationDelay: `${i * 120}ms` }}>
            <div className="mb-5 flex items-center justify-between">
              <Icon className="h-6 w-6 text-amber-400" />
              <span className="font-mono text-xs text-slate-600">0{i + 1}</span>
            </div>
            <h3 className="font-display text-lg font-semibold">{t(`landing.${k}t`)}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{t(`landing.${k}d`)}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const Example = () => {
  const { t, lang } = useI18n();
  const rows = [
    ["landing.pot", 2000, "text-slate-100"],
    ["landing.commission", -300, "text-red-400"],
    ["landing.winner", 1700, "text-emerald-400"],
  ];
  return (
    <div className="surface p-6" data-testid="landing-example">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{t("landing.exampleTitle")}</p>
      <p className="mt-2 font-display text-lg font-semibold">{t("landing.exampleLine")}</p>
      <div className="mt-5 space-y-3">
        {rows.map(([k, v, c]) => (
          <div key={k} className="flex items-center justify-between border-b border-white/5 pb-3 last:border-0">
            <span className="text-sm text-slate-400">
              {t(k)}
              {k === "landing.commission" ? " (15 %)" : ""}
            </span>
            <span className={`font-mono font-semibold ${c}`}>{formatXAF(v, lang)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Trust = () => {
  const { t } = useI18n();
  return (
    <section className="grid gap-4 py-6 md:grid-cols-3">
      <Example />
      <div className="surface p-6" data-testid="landing-fair">
        <Fingerprint className="h-6 w-6 text-amber-400" />
        <h3 className="mt-4 font-display text-lg font-semibold">{t("landing.fairTitle")}</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-400">{t("landing.fairText")}</p>
        <p className="mt-4 break-all font-mono text-[11px] text-slate-600">sha256(seed) = 9f2c…e41a</p>
        <Link to="/verify" className="mt-4 inline-flex min-h-[44px] items-center gap-2 text-sm font-semibold text-amber-400" data-testid="landing-verify-link">
          {t("verify.cta")} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="surface p-6" data-testid="landing-mobile-money">
        <Smartphone className="h-6 w-6 text-amber-400" />
        <h3 className="mt-4 font-display text-lg font-semibold">{t("landing.mmTitle")}</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-400">{t("landing.mmText")}</p>
        <div className="mt-5 flex gap-2">
          <span className="rounded-full bg-[#FFCC00] px-3 py-1 text-xs font-bold text-black">MTN MoMo</span>
          <span className="rounded-full bg-[#FF6600] px-3 py-1 text-xs font-bold text-white">Orange Money</span>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const { t } = useI18n();
  const links = ["terms", "rules", "privacy", "responsible"];
  return (
    <footer className="mt-10 flex flex-col gap-4 border-t border-white/10 pt-8 text-sm text-slate-500 md:flex-row md:items-center md:justify-between">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" /> {t("landing.adult")}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-2">
        {links.map((l) => (
          <Link key={l} to={`/legal/${l}`} className="transition-colors hover:text-amber-400" data-testid={`footer-link-${l}`}>
            {t(`legal.${l}`)}
          </Link>
        ))}
        <Link to="/verify" className="transition-colors hover:text-amber-400" data-testid="footer-link-verify">{t("verify.cta")}</Link>
        <a href={whatsappUrl(t("contact.message"))} target="_blank" rel="noopener noreferrer" aria-label={t("contact.cta")} title={t("contact.cta")}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366] text-white transition-transform hover:scale-105" data-testid="footer-whatsapp-link">
          <WhatsAppIcon className="h-5 w-5" />
        </a>
      </div>
    </footer>
  );
};

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <Hero />
      <GamesShowcase />
      <Rules />
      <Trust />
      <Footer />
    </div>
  );
}
